import React, { createContext, useContext } from 'react';
import { useForm, Controller, FieldValues, UseFormReturn, Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '../../utils/cn';

// Contexte pour passer control, errors et setValue aux FormField
interface FormContextType<T extends FieldValues> {
  control: UseFormReturn<T>['control'];
  errors: UseFormReturn<T>['formState']['errors'];
  setValue: UseFormReturn<T>['setValue'];
}

const FormContext = createContext<FormContextType<any> | null>(null);

// Hook pour utiliser le contexte du formulaire
export function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a Form component');
  }
  return context;
}

export interface FormFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'textarea' | 'select';
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  rows?: number;
  className?: string;
  disabled?: boolean;
}

export interface FormProps<T extends FieldValues> {
  schema: z.ZodSchema<T>;
  onSubmit: (data: T) => void | Promise<void>;
  children: React.ReactNode;
  className?: string;
  defaultValues?: Partial<T>;
}

export function FormField<T extends FieldValues>({
  name,
  label,
  type = 'text',
  placeholder,
  helpText,
  required = false,
  options = [],
  rows = 3,
  className,
  disabled = false
}: FormFieldProps<T>) {
  const { control, errors } = useFormContext();
  
  if (!name) {
    console.error('❌ FormField: name prop is undefined!');
    return <div className="text-red-500">Erreur: propriété 'name' manquante</div>;
  }
  
  const error = errors[name];
  const fieldId = `field-${name}`;

  return (
    <div className={cn('space-y-2', className)}>
      <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (type === 'textarea') {
            return (
              <textarea
                {...field}
                id={fieldId}
                rows={rows}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(
                  'block w-full rounded-lg border px-3 py-2 text-sm',
                  'focus:ring-2 focus:ring-primary/40 focus:border-primary',
                  'disabled:bg-gray-50 disabled:text-gray-500',
                  error
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/40'
                    : 'border-gray-300 focus:border-primary'
                )}
              />
            );
          }

          if (type === 'select') {
            return (
              <select
                {...field}
                id={fieldId}
                disabled={disabled}
                className={cn(
                  'block w-full rounded-lg border px-3 py-2 text-sm',
                  'focus:ring-2 focus:ring-primary/40 focus:border-primary',
                  'disabled:bg-gray-50 disabled:text-gray-500',
                  error
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/40'
                    : 'border-gray-300 focus:border-primary'
                )}
              >
                <option value="">Sélectionnez...</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            );
          }

          return (
            <input
              {...field}
              id={fieldId}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                'block w-full rounded-lg border px-3 py-2 text-sm',
                'focus:ring-2 focus:ring-primary/40 focus:border-primary',
                'disabled:bg-gray-50 disabled:text-gray-500',
                error
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500/40'
                  : 'border-gray-300 focus:border-primary'
              )}
            />
          );
        }}
      />

      {helpText && (
        <p className="text-sm text-gray-500">{helpText}</p>
      )}

      {error && (
        <p className="text-sm text-red-600">
          {typeof error === 'string' ? error : (error as any)?.message || 'Erreur de validation'}
        </p>
      )}
    </div>
  );
}

export function Form<T extends FieldValues>({
  schema,
  onSubmit,
  children,
  className,
  defaultValues
}: FormProps<T>) {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<T>({
    resolver: zodResolver(schema as any),
    defaultValues: defaultValues as any,
    mode: 'onChange'
  });

  const handleFormSubmit = async (data: T) => {
    try {
      await onSubmit(data);
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
    }
  };

  return (
    <FormContext.Provider value={{ control, errors, setValue }}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className={cn('space-y-6', className)}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}

// Composant de bouton de soumission
export interface SubmitButtonProps {
  children: React.ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

export function SubmitButton({
  children,
  isLoading = false,
  disabled = false,
  className,
  variant = 'primary'
}: SubmitButtonProps) {
  const baseClasses = 'inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
  };

  return (
    <button
      type="submit"
      disabled={disabled || isLoading}
      className={cn(baseClasses, variantClasses[variant], className)}
    >
      {isLoading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      )}
      {children}
    </button>
  );
}

// Composant de groupe de champs
export interface FormGroupProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  description?: string;
}

export function FormGroup({ children, className, title, description }: FormGroupProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {(title || description) && (
        <div className="space-y-1">
          {title && (
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          )}
          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}
        </div>
      )}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

// Composant de grille de champs
export interface FormGridProps {
  children: React.ReactNode;
  cols?: 1 | 2 | 3 | 4;
  className?: string;
}

export function FormGrid({ children, cols = 2, className }: FormGridProps) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={cn('grid gap-6', gridCols[cols], className)}>
      {children}
    </div>
  );
}

export default Form;
